import { Cell, Pie, PieChart, ResponsiveContainer } from "recharts";
import { PageHead } from "../GtaxShell";
import { CurveChart, MonthlyChart } from "../GtaxPerformance";
import { REPORT, usd } from "../data";

const STATS = [
  { k: usd(REPORT.net), v: "Net profit", id: "net" },
  { k: usd(REPORT.grossProfit), v: "Gross profit", id: "gross-profit" },
  { k: usd(REPORT.grossLoss), v: "Gross loss", id: "gross-loss" },
  { k: REPORT.profitFactor.toFixed(2), v: "Profit factor", id: "pf" },
  { k: REPORT.deals.toLocaleString("en-US"), v: "Deals", id: "deals" },
  { k: "$0.00", v: "Commissions · Swaps", id: "costs" },
];

const PENDING = ["Maximum drawdown", "Win rate", "Average win / loss", "Starting balance", "Long / short split"];

const Donut = () => (
  <div className="gx-card p-8 flex flex-col items-center" data-testid="gtax-report-donut">
    <div className="relative w-64 h-64">
      <ResponsiveContainer>
        <PieChart>
          <Pie data={[{ v: REPORT.grossProfit }, { v: Math.abs(REPORT.grossLoss) }]} dataKey="v" innerRadius={88} outerRadius={110} stroke="none" startAngle={90} endAngle={-270}>
            <Cell fill="#B8892B" /><Cell fill="#D9C9A8" />
          </Pie>
        </PieChart>
      </ResponsiveContainer>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="gx-serif text-3xl gx-gold">{usd(REPORT.net)}</div>
        <div className="text-xs text-[color:var(--mute)] uppercase tracking-widest">Net</div>
      </div>
    </div>
    <div className="mt-6 flex gap-8 text-sm">
      <span><span className="inline-block w-3 h-3 bg-[#B8892B] mr-2" />Gross profit</span>
      <span><span className="inline-block w-3 h-3 bg-[#D9C9A8] mr-2" />Gross loss</span>
    </div>
  </div>
);

export default function GtaxReport() {
  return (
    <div data-testid="gtax-report-page">
      <PageHead eyebrow="Performance report" title="The verified record."
        sub={`MT5 account history for the strategy, ${REPORT.period}. Instrument: ${REPORT.instrument}. Figures in US dollars.`} />
      <section className="pb-20">
        <div className="gx-wrap">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-[color:var(--line)]">
            {STATS.map((s) => (
              <div key={s.id} className="bg-[color:var(--bg)] p-7" data-testid={`gtax-report-${s.id}`}>
                <div className="text-xs tracking-widest uppercase text-[color:var(--mute)]">{s.v}</div>
                <div className="gx-serif text-3xl mt-2">{s.k}</div>
              </div>
            ))}
          </div>
          <div className="mt-10 grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2"><CurveChart h={400} /></div>
            <Donut />
          </div>
          <div className="mt-6 grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2"><MonthlyChart h={320} /></div>
            <div className="gx-card p-8" data-testid="gtax-report-symbols">
              <div className="gx-eyebrow">Symbols</div>
              <div className="mt-6 flex justify-between items-baseline"><span className="gx-serif text-2xl">XAUUSD</span><span className="gx-gold gx-serif text-2xl">100%</span></div>
              <div className="mt-3 h-2 bg-[color:var(--gold)] rounded" />
              <div className="mt-4 text-sm text-[color:var(--mute)]">{REPORT.deals.toLocaleString("en-US")} deals · {usd(REPORT.net)} net</div>
            </div>
          </div>
          <div className="mt-10 overflow-x-auto">
            <table className="w-full text-sm" data-testid="gtax-report-monthly-table">
              <thead><tr className="text-left text-xs tracking-widest uppercase text-[color:var(--mute)] border-b border-[color:var(--line)]"><th>Period</th><th className="text-right">Net profit</th><th className="text-right">Cumulative</th></tr></thead>
              <tbody>
                {REPORT.months.map((x, i) => (
                  <tr key={x.m} className="border-b border-[color:var(--line)]" data-testid={`gtax-report-row-${i}`}>
                    <td>{x.m} 2026</td><td className="text-right tabular-nums gx-gold">{usd(x.v)}</td><td className="text-right tabular-nums">{usd(REPORT.curve[i + 1].v)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-12 gx-card p-8" data-testid="gtax-report-pending">
            <div className="gx-eyebrow">Next release of this report</div>
            <p className="mt-4 text-[color:var(--mute)]">The following metrics will be added from the full MT5 statement export: {PENDING.join(" · ")}. September and October are shown combined until the monthly split is published.</p>
          </div>
          <p className="mt-8 text-xs text-[color:var(--mute)]">Gross strategy results on the trading account, before member profit-share. Past performance is not a reliable indicator of future results.</p>
        </div>
      </section>
    </div>
  );
}
